import { useState, useMemo } from 'react';

export type SortDir = 'asc' | 'desc';
export interface SortState { key: string | null; dir: SortDir; }

type SortValue = string | number | null | undefined;

/**
 * Client-side single-column sort for lists that are already fully loaded.
 * Ported from reporting_app/web/src/hooks/useTableSort.ts.
 *
 * `accessors` maps a column key to a value getter. Clicking a header toggles
 * asc -> desc; a different header starts at asc. Empty/null values always
 * sort last (both directions). Strings use a numeric-aware locale compare so
 * "DRA-2026-2" < "DRA-2026-10".
 *
 * CONTROLLABLE SINCE P30. Pass `controlled` and the hook holds no state of its
 * own: the caller owns the sort (the ticket boards keep it in the URL, so an
 * order can be shared and P31's "next in review" can walk it). Left out, it
 * behaves exactly as before — the other tables in the app are unchanged.
 */
export interface ControlledSort {
    sort: SortState | null;
    onSortChange: (next: SortState) => void;
}

/**
 * The comparator, as a pure function — the SAME one `useTableSort` runs (P31).
 *
 * Extracted so a screen that is not the table can reproduce the table's order.
 * `/review`'s "Previous / Next in review" walks the order the reviewer is
 * actually looking at, and it lives on the TICKET page, which has no table.
 * Re-implementing the comparator there would drift on exactly the cases the
 * rules below exist for — nils last in both directions, and a numeric-aware
 * compare so DRA-2026-2 sorts before DRA-2026-10.
 */
export function sortRows<T>(
    rows: T[],
    accessors: Record<string, (row: T) => SortValue>,
    sort: SortState | null,
): T[] {
    if (!sort?.key) return rows;
    const acc = accessors[sort.key];
    if (!acc) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = acc(a);
        const bv = acc(b);
        const aNil = av === null || av === undefined || av === '';
        const bNil = bv === null || bv === undefined || bv === '';
        if (aNil && bNil) return 0;
        if (aNil) return 1;   // nils last regardless of direction
        if (bNil) return -1;
        const base = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return base * dir;
    });
}

export function useTableSort<T>(
    rows: T[],
    accessors: Record<string, (row: T) => SortValue>,
    initial: SortState = { key: null, dir: 'asc' },
    controlled?: ControlledSort,
) {
    const [internal, setInternal] = useState<SortState>(initial);
    const sort = controlled ? (controlled.sort ?? initial) : internal;

    const toggleSort = (key: string) => {
        const next: SortState = sort.key === key
            ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'asc' };
        if (controlled) controlled.onSortChange(next);
        else setInternal(next);
    };

    const sorted = useMemo(
        () => sortRows(rows, accessors, sort),
        // accessors is a stable literal per render; intentionally excluded
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, sort],
    );

    return { sorted, sort, toggleSort };
}
